/**
 * Microsoft Graph Client Service
 *
 * Provides client-side methods to interact with Microsoft Graph API
 * using the current user's access token obtained via OIDC.
 */

import { Client, GraphError } from '@microsoft/microsoft-graph-client';
import OidcAuthService from './oidcAuth';

export interface GraphUser {
  id: string;
  displayName: string;
  mail?: string;
  userPrincipalName?: string;
  givenName?: string;
  surname?: string;
  jobTitle?: string;
  department?: string;
  officeLocation?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  streetAddress?: string;
  companyName?: string;
  employeeId?: string;
  employeeType?: string;
  mobilePhone?: string;
  businessPhones?: string[];
  faxNumber?: string;
  accountEnabled?: boolean;
  createdDateTime?: string;
  lastSignInDateTime?: string;
  onPremisesSamAccountName?: string;
  onPremisesDistinguishedName?: string;
  preferredLanguage?: string;
  usageLocation?: string;
  mailNickname?: string;
}

export interface GraphGroup {
  id: string;
  displayName: string;
  description?: string;
  mail?: string;
}

export interface GraphOrganization {
  id: string;
  displayName: string;
  verifiedDomains: Array<{
    capabilities: string;
    isDefault: boolean;
    isInitial: boolean;
    name: string;
    type: string;
  }>;
}

class GraphClientService {
  private static instance: GraphClientService;

  private constructor() {}

  static getInstance(): GraphClientService {
    if (!GraphClientService.instance) {
      GraphClientService.instance = new GraphClientService();
    }
    return GraphClientService.instance;
  }

  /**
   * Create a Graph client with the current user's access token.
   */
  private async createClient(): Promise<Client> {
    const oidcService = OidcAuthService.getInstance();
    const user = await oidcService.getCurrentUser();

    if (!user || !user.access_token) {
      throw new Error('No access token available. Please sign in.');
    }

    return Client.init({
      authProvider: (done) => {
        done(null, user.access_token);
      },
    });
  }

  async getCurrentUserProfile(fields?: string[]): Promise<GraphUser> {
    try {
      const client = await this.createClient();
      let request = client.api('/me');

      if (fields && fields.length > 0) {
        request = request.select(fields.join(','));
      }

      const user = await request.get();
      return user as GraphUser;
    } catch (error) {
      console.error('Error getting current user profile:', error);
      throw this.handleGraphError(error);
    }
  }

  async getCurrentUserPhoto(): Promise<string | null> {
    try {
      const client = await this.createClient();
      const photo = await client.api('/me/photo/$value').get();

      if (!photo) {
        return null;
      }

      const blob = new Blob([photo], { type: 'image/jpeg' });
      return URL.createObjectURL(blob);
    } catch (error: any) {
      if (error.statusCode === 404) {
        console.info('User has no profile photo');
        return null;
      }
      console.error('Error getting current user photo:', error);
      throw this.handleGraphError(error);
    }
  }

  async getCurrentUserManager(): Promise<GraphUser | null> {
    try {
      const client = await this.createClient();
      const manager = await client.api('/me/manager').get();
      return manager as GraphUser;
    } catch (error: any) {
      if (error.statusCode === 404) {
        console.info('User has no manager configured');
        return null;
      }
      console.error('Error getting current user manager:', error);
      throw this.handleGraphError(error);
    }
  }

  async getCurrentUserGroups(): Promise<GraphGroup[]> {
    try {
      const client = await this.createClient();
      const response = await client.api('/me/memberOf').get();
      return response.value as GraphGroup[];
    } catch (error) {
      console.error('Error getting current user groups:', error);
      throw this.handleGraphError(error);
    }
  }

  /**
   * Get a user by their email address or user principal name
   */
  async getUserByEmail(email: string, fields?: string[]): Promise<GraphUser | null> {
    try {
      const client = await this.createClient();
      let request = client.api(`/users/${encodeURIComponent(email)}`);

      if (fields && fields.length > 0) {
        request = request.select(fields.join(','));
      }

      const user = await request.get();
      return user as GraphUser;
    } catch (error: any) {
      if (error.statusCode === 404) {
        console.info(`User not found: ${email}`);
        return null;
      }
      console.error(`Error getting user by email ${email}:`, error);
      throw this.handleGraphError(error);
    }
  }

  /**
   * Get multiple users by their email addresses
   * Returns an array of results with email and user data (or error)
   */
  async getUsersByEmails(emails: string[], fields?: string[]): Promise<Array<{
    email: string;
    user: GraphUser | null;
    error?: string;
  }>> {
    const results: Array<{ email: string; user: GraphUser | null; error?: string }> = [];
    
    // Process in parallel but with some throttling to avoid rate limits
    const batchSize = 5;
    for (let i = 0; i < emails.length; i += batchSize) {
      const batch = emails.slice(i, i + batchSize);
      const batchResults = await Promise.all(
        batch.map(async (email) => {
          try {
            const user = await this.getUserByEmail(email.trim(), fields);
            return { email: email.trim(), user, error: user ? undefined : 'User not found' };
          } catch (error: any) {
            return { email: email.trim(), user: null, error: error.message || 'Failed to fetch user' };
          }
        })
      );
      results.push(...batchResults);
    }
    
    return results;
  }

  /**
   * Search for users by name or email
   */
  async searchUsers(query: string, limit: number = 10): Promise<GraphUser[]> {
    try {
      const client = await this.createClient();
      const response = await client
        .api('/users')
        .filter(`startswith(displayName,'${query}') or startswith(mail,'${query}') or startswith(userPrincipalName,'${query}')`)
        .top(limit)
        .select('id,displayName,mail,userPrincipalName,jobTitle,department,officeLocation')
        .get();
      return response.value as GraphUser[];
    } catch (error) {
      console.error('Error searching users:', error);
      throw this.handleGraphError(error);
    }
  }

  private handleGraphError(error: any): Error {
    if (error instanceof GraphError) {
      const statusCode = error.statusCode;
      let message = error.message || 'Microsoft Graph API error';

      switch (statusCode) {
        case 401:
          message = 'Authentication failed. Please sign in again.';
          break;
        case 403:
          message = 'Insufficient permissions to access this resource.';
          break;
        case 404:
          message = 'Resource not found.';
          break;
        case 429:
          message = 'Too many requests. Please try again later.';
          break;
        case 503:
          message = 'Microsoft Graph service is temporarily unavailable.';
          break;
        default:
          if (statusCode && statusCode >= 500) {
            message = 'Microsoft Graph service error. Please try again.';
          }
      }

      const standardError: any = new Error(message);
      standardError.statusCode = statusCode;
      standardError.isGraphError = true;
      return standardError;
    }

    return error instanceof Error ? error : new Error('Unknown Microsoft Graph API error');
  }
}

export default GraphClientService;



